import { useApolloClient } from "@apollo/client";
import { Form, Input, Modal, Radio, Select, Table, Tag } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import Header from "shared/components/Header";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import cuid from "cuid";

const STATE_COLORS = {
  draft: { dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280", label: "Brouillon" },
  running: { dot: "#27ae60", bg: "#ecfdf5", text: "#065f46", label: "En cours" },
  paused: { dot: "#f2994a", bg: "#fff8ee", text: "#92400e", label: "En pause" },
  completed: { dot: "#6366f1", bg: "#eef2ff", text: "#3730a3", label: "Terminé" },
  archived: { dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280", label: "Archivé" },
};

const View = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const { data: campaignsData, refetch } = useQuery(graphql.queries.CRM_CAMPAIGNS, {
    variables: {},
    fetchPolicy: "network-only",
  });
  const { data: templatesData } = useQuery(graphql.queries.CRM_SEQUENCE_TEMPLATES, { variables: {} });

  const [createCampaign] = useMutation(graphql.mutations.CREATE_CRM_CAMPAIGN);
  const [createSequence] = useMutation(graphql.mutations.CREATE_CRM_SEQUENCE);
  const [createStep] = useMutation(graphql.mutations.CREATE_CRM_STEP);

  const campaigns = get(campaignsData, "crmCampaigns.data") || [];
  const templates = get(templatesData, "crmSequenceTemplates.data") || [];

  const filtered = campaigns
    .filter((c) => filter === "all" || c.state === filter)
    .filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase()));

  const handleCreate = async () => {
    const values = await form.validateFields();
    const campaignId = cuid();
    const sequenceId = cuid();

    try {
      await createSequence({ variables: { data: { id: sequenceId, campaignId } } });
      await createCampaign({
        variables: {
          data: {
            id: campaignId,
            name: values.name,
            state: "draft",
            sequenceId,
            templateId: values.templateId || null,
            config: {
              stopOnReply: true, stopOnBounce: true, stopOnUnsubscribe: true,
              stopOnMeetingBooked: false, trackOpens: true, trackClicks: true,
              dailySendLimit: 50, maxLeadsInSequence: null,
              unsubscribeScope: "campaign", unsubscribeLink: true,
            },
            health: {},
          },
        },
      });

      if (values.templateId) {
        const tpl = templates.find((t) => t.id === values.templateId);
        if (tpl && tpl.sequenceId) {
          const { data: seqData } = await client.query({
            query: graphql.queries.CRM_SEQUENCE,
            variables: { where: { id: tpl.sequenceId } },
            fetchPolicy: "network-only",
          });
          const tplSteps = get(seqData, "crmSequence.steps") || [];
          const idMap = {};
          for (const step of tplSteps) {
            const newId = cuid();
            idMap[step.id] = newId;
          }
          for (const step of tplSteps) {
            await createStep({
              variables: {
                data: {
                  id: idMap[step.id],
                  type: step.type,
                  order: step.order,
                  parentId: step.parentId ? idMap[step.parentId] || null : null,
                  branchKey: step.branchKey,
                  config: step.config,
                  isAutomatic: step.isAutomatic,
                  senderOverride: step.senderOverride,
                  sequence: { id: sequenceId },
                },
              },
            });
          }
        }
      }

      form.resetFields();
      setCreateOpen(false);
      await refetch();
      navigate(`${SCREENS.PRIVATE.ADMIN.CAMPAIGNS.DETAIL.path}/${campaignId}`);
    } catch (err) { /* handled by hook */ }
  };

  return (
    <>
      <Header key="header" title="Campagnes" actions={[{ text: "Nouvelle campagne", onClick: () => setCreateOpen(true), type: "primary", icon: <BiPlus size={18} /> }]} />

      <div className="m-bottom-16 d-flex justify--between" style={{ alignItems: "center" }}>
        <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} buttonStyle="solid" size="small">
          <Radio.Button value="all">Tous</Radio.Button>
          <Radio.Button value="draft">Brouillon</Radio.Button>
          <Radio.Button value="running">En cours</Radio.Button>
          <Radio.Button value="paused">En pause</Radio.Button>
          <Radio.Button value="completed">Terminé</Radio.Button>
        </Radio.Group>
        <Input.Search placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} allowClear style={{ width: 250 }} />
      </div>

      <Table dataSource={filtered} rowKey="id" size="small"
        onRow={(record) => ({ onClick: () => navigate(`${SCREENS.PRIVATE.ADMIN.CAMPAIGNS.DETAIL.path}/${record.id}`), style: { cursor: "pointer" } })}
        columns={[
          {
            title: "Nom",
            dataIndex: "name",
            render: (name, record) => (
              <div>
                <div style={{ fontWeight: 500 }}>{name}</div>
                {record.templateId && (
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>
                    {(templates.find((t) => t.id === record.templateId) || {}).name || "Personnalisé"}
                  </div>
                )}
              </div>
            ),
          },
          {
            title: "État",
            dataIndex: "state",
            width: 120,
            render: (state) => {
              const s = STATE_COLORS[state] || STATE_COLORS.draft;
              return (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
                  padding: "3px 10px 3px 8px", borderRadius: 20, background: s.bg, color: s.text,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
                  {s.label}
                </span>
              );
            },
          },
          {
            title: "Expéditeurs",
            dataIndex: "senderIds",
            width: 80,
            render: (ids) => (ids || []).length,
          },
          {
            title: "Leads",
            dataIndex: ["health", "totalLeads"],
            width: 70,
            align: "right",
          },
          {
            title: "Santé",
            width: 120,
            render: (_, record) => {
              const stuck = get(record, "health.stuckLeads", 0);
              const errors = get(record, "health.errorCount", 0);
              if (record.state === "draft") return <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>;
              if (stuck > 0 || errors > 0) {
                const parts = [];
                if (stuck > 0) parts.push(`${stuck} stuck`);
                if (errors > 0) parts.push(`${errors} err`);
                return <span style={{ fontSize: 12, color: stuck > 0 ? "#f2994a" : "#eb5757", fontWeight: 500 }}>{parts.join(" · ")}</span>;
              }
              return <span style={{ fontSize: 12, color: "#27ae60", fontWeight: 500 }}>OK</span>;
            },
          },
          {
            title: "Envois",
            width: 120,
            render: (_, record) => {
              const sent = get(record, "health.sentToday", 0);
              const limit = get(record, "health.dailyLimit", 0);
              if (!limit) return <span style={{ color: "#9ca3af" }}>—</span>;
              const pct = Math.min((sent / limit) * 100, 100);
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: "#e8ecf0", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: pct > 90 ? "#f2994a" : "#1a9bdb", borderRadius: 2, transition: "width .3s" }} />
                  </div>
                  <span style={{ fontSize: 11, color: "#9ca3af", minWidth: 44, textAlign: "right" }}>{sent}/{limit}</span>
                </div>
              );
            },
          },
          {
            title: "Dernière",
            dataIndex: ["health", "lastActivityAt"],
            width: 90,
            render: (d) => {
              if (!d) return <span style={{ color: "#9ca3af" }}>—</span>;
              const diff = Math.round((Date.now() - new Date(d).getTime()) / 60000);
              if (diff < 60) return <span style={{ color: "#6b7280" }}>{diff} min</span>;
              if (diff < 1440) return <span style={{ color: "#6b7280" }}>{Math.round(diff / 60)}h</span>;
              return <span style={{ color: "#6b7280" }}>{Math.round(diff / 1440)}j</span>;
            },
          },
        ]}
      />

      <Modal title="Nouvelle campagne" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={handleCreate} okText="Créer" cancelText="Annuler">
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nom de la campagne" rules={[{ required: true, message: "Le nom est requis" }]}>
            <Input placeholder="Ex: Q2 SaaS Outreach" />
          </Form.Item>
          <Form.Item name="templateId" label="À partir d'un modèle (optionnel)">
            <Select placeholder="Séquence vierge" allowClear>
              {templates.map((t) => <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default View;
